import type { ReactNode } from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

const baseCss = css({
	margin: 0,
	fontFamily: 'var(--atlassian-sans-font-family)',
	color: token('color.text.subtle'),
});

const smallCss = css({
	fontSize: '0.875rem',
	lineHeight: '1.25rem',
});

const mediumCss = css({
	fontSize: '1rem',
	lineHeight: '1.5rem',
});

const alignLeftCss = css({ textAlign: 'left' });
const alignCenterCss = css({ textAlign: 'center' });

export type LoomOnboardingTextProps = {
	children: ReactNode;
	size?: 'sm' | 'md';
	align?: 'left' | 'center';
	/**
	 * Render as `<span>` (inline) instead of the default `<div>` (block).
	 * Use inside `<Inline>` / list rows where a block element would break
	 * baseline alignment with sibling icons.
	 */
	inline?: boolean;
	/**
	 * Override the default `color.text.subtle`. Prefer a design token, but
	 * a raw hex is accepted for one-off matches to the mock (e.g. the
	 * value-prop blurb under the role select).
	 */
	color?: string;
};

/**
 * Reusable body / subtle text for the Loom onboarding flow.
 *
 * Sizes:
 * - `sm` (default) → 14/20, used for value-prop rows in RecorderCard.
 * - `md`           → 16/24, used for the subheader under the section
 *                    <h2>.
 *
 * Always Atlassian Sans + `color.text.subtle`, with `margin: 0` so the
 * parent `<Stack>` controls vertical rhythm.
 */
export const LoomOnboardingText = ({
	children,
	size = 'sm',
	align = 'left',
	inline = false,
	color,
}: LoomOnboardingTextProps) => {
	const styles = [
		baseCss,
		size === 'md' ? mediumCss : smallCss,
		align === 'center' ? alignCenterCss : alignLeftCss,
	];

	if (inline) {
		return (
			<span css={styles} style={color ? { color } : undefined}>
				{children}
			</span>
		);
	}

	return (
		<div css={styles} style={color ? { color } : undefined}>
			{children}
		</div>
	);
};
